import { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  UserCircle, Briefcase, SignOut, Pulse,
  PaperPlaneTilt, UsersThree, Plug, ArrowsLeftRight,
  XCircle, Lightning, Gauge, ShieldStar, ChatCircle,
  List, X as IconX, Key, Clock, PlugsConnected,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Footer } from "@/components/Brand";
import api, { formatApiError } from "@/lib/api";
import useSmartPoll from "@/lib/useSmartPoll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { corporateInput } from "@/components/Shared";
import { toast } from "sonner";

function buildSections(t) {
  return [
    {
      title: t("nav.group.panel", "Panel"),
      items: [
        { to: "/support", label: t("nav.dashboard"), icon: Gauge, testid: "snav-dashboard", exact: true },
      ],
    },
    {
      title: t("snav.group.operations"),
      items: [
        { to: "/support/orders", label: t("snav.orders"), icon: PaperPlaneTilt, testid: "snav-orders", badge: "pending_orders" },
        { to: "/support/leads", label: t("snav.leads"), icon: Briefcase, testid: "snav-leads", badge: "active_leads" },
      ],
    },
    {
      title: t("snav.group.clients"),
      items: [
        { to: "/support/clients/active", label: t("snav.active"), icon: Plug, testid: "snav-active", dotColor: "bg-emerald-400" },
        { to: "/support/clients/pending", label: t("nav.clients.pending", "Pendientes de activación"), icon: Clock, testid: "snav-pending-activation", dotColor: "bg-amber-400", badge: "pending_activation", badgeTone: "amber" },
        { to: "/support/clients/pending_reconnection", label: t("nav.clients.reconnect"), icon: PlugsConnected, testid: "snav-pending-reconnection", dotColor: "bg-indigo-400", badge: "pending_reconnection", badgeTone: "indigo" },
        { to: "/support/clients/disconnected", label: t("snav.disconnected"), icon: XCircle, testid: "snav-disconnected", dotColor: "bg-red-400", badge: "disconnected_clients", badgeTone: "rose" },
        { to: "/support/clients/transferred", label: t("snav.transferred"), icon: ArrowsLeftRight, testid: "snav-transferred", dotColor: "bg-slate-400", badge: "transferred_clients" },
        { to: "/support/leads/lost", label: t("snav.lost"), icon: UserCircle, testid: "snav-lost", dotColor: "bg-slate-500" },
      ],
    },
    {
      title: t("snav.group.catalog"),
      items: [
        { to: "/support/plans", label: t("snav.plans.view"), icon: Lightning, testid: "snav-plans" },
      ],
    },
    {
      title: t("nav.group.communication"),
      items: [
        { to: "/support/communications", label: t("nav.communications"), icon: ChatCircle, testid: "snav-comms", badge: "unread_messages" },
      ],
    },
    {
      title: t("snav.group.team", "Equipo"),
      items: [
        { to: "/support/users", label: t("snav.users"), icon: UsersThree, testid: "snav-users" },
      ],
    },
    {
      title: t("nav.group.admin", "Administración"),
      items: [
        { to: "/support/activity", label: t("nav.activity"), icon: Pulse, testid: "snav-activity" },
      ],
    },
  ];
}

function isActive(pathname, to, exact) {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

export default function SupportLayout({ children }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [kpis, setKpis] = useState({ pending_orders: 0, active_leads: 0, unread_messages: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const submitPasswordChange = async () => {
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error(t("pwd.mismatch", "Las contraseñas no coinciden")); return; }
    if (pwdForm.new_password.length < 8) { toast.error(t("pwd.tooShort", "Mínimo 8 caracteres")); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success(t("pwd.success", "Contraseña actualizada"));
      setPwdOpen(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPwdSaving(false); }
  };

  const SECTIONS = buildSections(t);

  useSmartPoll(() => {
    Promise.all([
      api.get("/support/kpis").catch(() => ({ data: {} })),
      api.get("/threads/summary/unread").catch(() => ({ data: {} })),
    ]).then(([k, u]) => {
      setKpis({ ...(k.data || {}), unread_messages: u.data?.unread_messages || 0 });
    }).catch(() => {});
  }, 60000);

  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  const handleLogout = async () => { await logout(); navigate("/support/login"); };

  const isSupportAdmin = user?.role === "support_admin" || user?.role === "super_admin";
  // Pre-filter sections per role so we don't re-allocate on every render.
  const visibleSections = useMemo(
    () => SECTIONS.filter(s => !s.requireRole || isSupportAdmin),
    [isSupportAdmin]
  );

  const SidebarContent = (
    <>
      <div className="px-5 h-16 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/10 border border-white/15 text-white grid place-items-center">
            <ShieldStar size={16} weight="regular" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight text-white leading-none">FLEX ENERGY</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold mt-1">{t("brand.support")}</div>
          </div>
        </div>
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden text-slate-300 hover:text-white"
          aria-label="Cerrar menú"
          data-testid="support-sidebar-close"
        >
          <IconX size={18} />
        </button>
      </div>

      <nav className="flex-1 min-h-0 px-3 py-5 space-y-6 overflow-y-auto sidebar-scroll">
        {visibleSections.map((section, sIdx) => (
          <div key={section.title || `section-${sIdx}`}>
            {section.title && (
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                {section.title}
              </div>
            )}
            <div>
              {section.items.map(({ to, label, icon: Icon, testid, exact, badge, badgeTone, dotColor }) => {
                const active = isActive(pathname, to, exact);
                const badgeValue = badge ? kpis[badge] : null;
                const toneClass = badgeTone === "rose"
                  ? "bg-rose-500 text-white"
                  : badgeTone === "indigo"
                    ? "bg-indigo-400 text-slate-900"
                    : "bg-amber-400 text-slate-900";
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testid}
                    className={`relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-colors ${
                      active
                        ? "text-white bg-white/[0.06]"
                        : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-white" />
                    )}
                    {dotColor && (
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} aria-hidden />
                    )}
                    {!dotColor && (
                      <Icon size={16} weight="regular" className={active ? "text-white" : "text-slate-400"} />
                    )}
                    <span className="flex-1 truncate">{label}</span>
                    {badgeValue > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full ${toneClass}`} data-testid={`${testid}-badge`}>
                        {badgeValue}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Session — just below last item */}
        <div>
          <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
            {isSupportAdmin ? t("snav.session.admin") : t("snav.session.agent")}
          </div>
          <div className="px-3 py-1 mb-1">
            <div className="text-xs font-medium text-slate-100 truncate">{user?.name || user?.email}</div>
          </div>
          <Link
            to="/support/security"
            data-testid="support-security-link"
            className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium text-slate-300 hover:bg-white/[0.04] hover:text-white transition-colors"
          >
            <Key size={16} className="text-slate-400" />
            <span className="flex-1 text-left">{t("nav.security", "Seguridad")}</span>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="support-logout"
            className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium text-slate-300 hover:bg-white/[0.04] hover:text-white transition-colors"
          >
            <SignOut size={16} className="text-slate-400" />
            <span className="flex-1 text-left">{t("nav.logout")}</span>
          </button>
        </div>
      </nav>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 transition-colors">
      {/* Desktop sidebar — sticky, own scroll */}
      <aside
        className="w-60 hidden lg:flex shrink-0 flex-col text-slate-300 border-r border-slate-800 sticky top-0 h-screen"
        style={{ background: "#0A2540" }}
        data-testid="support-sidebar"
      >
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="support-sidebar-drawer">
          <div className="w-60 h-screen flex flex-col text-slate-300 border-r border-slate-800 animate-in slide-in-from-left duration-200" style={{ background: "#0A2540" }}>
            {SidebarContent}
          </div>
          <div className="flex-1 bg-slate-950/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden h-14 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition-colors">
          <button
            onClick={() => setDrawerOpen(true)}
            className="text-slate-700 dark:text-slate-300 p-1.5 -ml-1.5"
            data-testid="support-open-sidebar"
            aria-label="Abrir menú"
          >
            <List size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-800 text-amber-400 grid place-items-center">
              <ShieldStar size={12} weight="regular" />
            </div>
            <div className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">FLEX · {t("brand.support")}</div>
          </div>
          <div className="w-7" />
        </div>

        <div className="flex-1">{children}</div>
        <Footer />
      </main>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("pwd.title", "Cambiar contraseña")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.current", "Contraseña actual")} *</Label>
              <PasswordInput autoComplete="current-password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className={corporateInput} data-testid="support-pwd-current" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.new", "Nueva contraseña")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className={corporateInput} data-testid="support-pwd-new" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.confirm", "Confirmar nueva")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className={corporateInput} data-testid="support-pwd-confirm" />
            </div>
            <p className="text-[10px] text-slate-500">{t("pwd.hint", "Mínimo 8 caracteres. Diferente a la actual.")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdSaving}>{t("common.cancel")}</Button>
            <Button onClick={submitPasswordChange} disabled={pwdSaving || !pwdForm.current_password || !pwdForm.new_password} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="support-pwd-submit">
              {pwdSaving ? t("common.saving") : t("pwd.submit", "Actualizar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
